import type { MergeElementProps } from '@mogul/shared';
import clsx from 'clsx';
import { createElement, forwardRef } from 'react';

import styles from './Input.module.css';

export interface InputRenderProps {
  className?: string;
}

export type InputProps<
  T extends React.ElementType<InputRenderProps> = 'input',
> = MergeElementProps<
  T,
  {
    /**
     * Override the root element type.
     *
     * @default input
     */
    Component?: T;

    /**
     * @deprecated Avoid the name attribute to prevent default autocomplete behavior.
     * @internal
     */
    name?: string;
  }
>;

/**
 * Basic styled `input` component. Use `InputField` for a more complete reusable
 * form input component including a label.
 */
export const Input = forwardRef(
  <T extends React.ElementType<InputRenderProps> = 'input'>(
    props: InputProps<T>,
    ref: React.ForwardedRef<React.ElementRef<T>>,
  ) => {
    const { Component = 'input', className, ...baseProps } = props;

    const renderProps: InputRenderProps = {
      ref,
      className: clsx(className, styles.root),
      ...baseProps,
    };

    return createElement(Component, renderProps);
  },
);
